/* ==========================================================================
   Event Modal - Asian American Forward Website
   ========================================================================== */

import { preventModalScroll, allowModalScroll } from './utils/scroll-prevention.js';

let eventModal = null;
let eventModalOverlay = null;
let eventModalClose = null;
let eventModalImage = null;
let eventModalTitle = null;
let eventDetailsBtn = null;
let currentEventType = null; // Store current event type for button functionality

/* ==========================================================================
   Event Modal Initialization
   ========================================================================== */

export function initializeEventModal() {
  console.log('🎯 Initializing event modal...');
  
  // Get modal elements
  eventModal = document.getElementById('event-modal');
  eventModalOverlay = document.querySelector('.event-modal-overlay');
  eventModalClose = document.querySelector('.event-modal-close');
  eventModalImage = document.getElementById('event-modal-image');
  eventModalTitle = document.getElementById('event-modal-title');
  eventDetailsBtn = document.getElementById('event-details-btn');
  
  if (!eventModal) {
    console.warn('Event modal not found');
    return;
  }
  
  // Set up event listeners
  setupEventModalListeners();
  
  // Set up event card click listeners (simplified, no delay needed)
  setupEventCardListeners();
  
  console.log('✅ Event modal initialized successfully');
}

/* ==========================================================================
   Event Modal Event Listeners
   ========================================================================== */

function setupEventModalListeners() {
  // Close modal when clicking overlay
  if (eventModalOverlay) {
    eventModalOverlay.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      closeEventModal();
    });
  }
  
  // Close modal when clicking close button
  if (eventModalClose) {
    eventModalClose.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      closeEventModal();
    });
  }
  
  // Close modal with Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && eventModal && eventModal.getAttribute('aria-hidden') === 'false') {
      e.preventDefault();
      e.stopPropagation();
      closeEventModal();
    }
  });
  
  // Handle "View Event Details" button click
  if (eventDetailsBtn) {
    eventDetailsBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      // Check which event this is and open the appropriate donation link
      if (currentEventType === '2025aspire') {
        // Open ActBlue donation link for ASPIRE PAC
        window.open('https://secure.actblue.com/donate/aafaspiresf25', '_blank');
        console.log('🎯 Opening ASPIRE PAC donation link');
      } else if (currentEventType === 'tedlieu') {
        // Open ActBlue donation link for Ted Lieu
        window.open('https://secure.actblue.com/donate/tedlieu24', '_blank');
        console.log('🎯 Opening Ted Lieu donation link');
      } else if (currentEventType === 'andykim') {
        // Open ActBlue donation link for Andy Kim
        window.open('https://secure.actblue.com/donate/andykim24', '_blank');
        console.log('🎯 Opening Andy Kim donation link');
      } else {
        // For any future events, just log for now
        console.log('View Event Details clicked for:', currentEventType);
        // Future: Can add other event-specific links here
      }
    });
  }
  
  // Allow click-to-close on modal layout but prevent on button
  const eventModalLayout = document.querySelector('.event-modal-layout');
  if (eventModalLayout) {
    eventModalLayout.addEventListener('click', function(e) {
      // Only close if clicking directly on the layout (not on child elements)
      if (e.target === eventModalLayout) {
        e.preventDefault();
        e.stopPropagation();
        closeEventModal();
      }
    });
  }
  
  // Allow click-to-close on image container
  const eventModalImageContainer = document.querySelector('.event-modal-image-container');
  if (eventModalImageContainer) {
    eventModalImageContainer.addEventListener('click', function(e) {
      // Close if clicking directly on the container OR on the image
      if (e.target === eventModalImageContainer || e.target === eventModalImage) {
        e.preventDefault();
        e.stopPropagation();
        closeEventModal();
      }
    });
  }
  
  // Prevent clicks inside modal content from closing the modal (except layout and image container)
  const eventModalContent = document.querySelector('.event-modal-content');
  if (eventModalContent) {
    eventModalContent.addEventListener('click', function(e) {
      // Don't stop propagation if clicking on layout or image container
      if (e.target !== eventModalLayout && e.target !== eventModalImageContainer) {
        e.stopPropagation();
      }
    });
  }
}

function setupEventCardListeners() {
  const eventCards = document.querySelectorAll('.modal-event-card');
  
  console.log(`🎯 Found ${eventCards.length} modal event cards for modal setup`);
  
  eventCards.forEach((card, index) => {
    // Ensure this is actually a modal event card
    if (!card.classList.contains('modal-event-card')) {
      console.log('🚫 Skipping non-modal-event card');
      return;
    }
    
    console.log(`🎯 Setting up modal event card ${index + 1}:`, card.getAttribute('data-event'));
    
    // Simple, direct click handler - no cloning or complex event handling
    card.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      
      console.log('🎯 Modal event card clicked!', card.getAttribute('data-event'));
      
      const eventType = card.getAttribute('data-event');
      const eventImg = card.querySelector('.modal-event-img');
      const eventTitle = card.querySelector('.modal-event-title');
      
      if (eventImg && eventTitle) {
        console.log('🎯 Opening event modal for:', eventType);
        openEventModal(
          eventImg.src,
          eventImg.alt,
          eventTitle.textContent,
          eventType
        );
      } else {
        console.error('🚫 Modal event card missing image or title:', eventType);
      }
      
      return false;
    });
    
    // Add accessibility attributes
    card.style.cursor = 'pointer';
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.setAttribute('aria-label', `View ${card.getAttribute('data-event')} event details`);
  });
}

/* ==========================================================================
   Event Modal Open/Close Functions
   ========================================================================== */

function openEventModal(imageSrc, imageAlt, title, eventType) {
  if (!eventModal) return;
  
  // Store current event type for button functionality
  currentEventType = eventType;
  
  // Set modal content
  if (eventModalImage) {
    eventModalImage.src = imageSrc;
    eventModalImage.alt = imageAlt;
  }
  
  if (eventModalTitle) {
    eventModalTitle.textContent = title;
  }
  
  // Update button text based on event type
  if (eventDetailsBtn) {
    if (eventType === '2025aspire') {
      // Upcoming event - keep "Donate Here"
      eventDetailsBtn.textContent = 'Donate Here';
    } else {
      // Past events - change to "View More"
      eventDetailsBtn.textContent = 'View More';
    }
  }
  
  // Show modal and prevent scrolling
  eventModal.setAttribute('aria-hidden', 'false');
  preventModalScroll();
  
  // Focus management for accessibility (prevent scroll)
  eventModalClose?.focus({ preventScroll: true });
  
  console.log('🎯 Event modal opened:', title);
}

function closeEventModal() {
  if (!eventModal) return;
  
  // Hide modal and re-enable scrolling
  eventModal.setAttribute('aria-hidden', 'true');
  allowModalScroll();
  
  // Reset modal content
  if (eventModalImage) {
    eventModalImage.src = '';
    eventModalImage.alt = '';
  }
  
  if (eventModalTitle) {
    eventModalTitle.textContent = '';
  }
  
  // Reset button text to default
  if (eventDetailsBtn) {
    eventDetailsBtn.textContent = 'Donate Here';
  }
  
  // Clear current event type
  currentEventType = null;
  
  console.log('🎯 Event modal closed');
}

/* ==========================================================================
   Event Modal Utilities
   ========================================================================== */

/* ==========================================================================
   Error Handling
   ========================================================================== */

export function handleEventModalError(error) {
  console.error('Event modal error:', error);
  
  // Close modal if there's an error
  if (eventModal && eventModal.getAttribute('aria-hidden') === 'false') {
    closeEventModal();
  }
} 